import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { concat } from 'ramda';
import { uniqBy } from 'lodash';

import { Paper, Grid, Typography, IconButton, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, CircularProgress, FormControlLabel } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import Filter from '../../../components/Filter/components';
import FilterMobile from '../../../components/FilterMobile/components';
import Modal from '../../../components/Modal';
import TableMobile from '../../../components/TableMobile';
import Button from '../../../components/Button';
import ServiceOrder from '../../../components/ServiceOrder/components';
import Invoice from '../../../components/Invoice';
import InvoiceServiceContract from '../../../components/InvoiceServiceContract';
import SellOrder from '../../../components/SellOrder/components';
import Receipt from '../../Transactions/components/Receipt';
import Payment from '../../Transactions/components/Payment';
import ProvisionalReceiptError from './ProvisionalReceiptError';
import ProvisionalReceiptCancel from './ProvisionalReceiptCancel';
import Resume from './Resume';
import LoaderLabel from 'components/LoaderLabel';
import WarningMessage from 'components/WarningMessage';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faExclamationCircle, faTimesCircle, faDownload, faCog, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';

import { loadProvisionalReceipt, loadProvisionalReceiptResume, loadDownloadNfse, resendMultipleNfse } from '../store/thunk';
import { handleError, setIsNfSendModalOpen, setSelectedSingleNfWithErrors, setSelectedNfWithErrors, setSelectedNfWithErrorsPaymentDateTime, clearNfWithErrors, clearPageNfWithErrors } from '../store/actions';
import { formatFilters } from '../../../helpers/formatters';
import { convertList } from '../../../helpers/converters';
import { relativePercentage } from '../../../helpers/common';
import { loadFilters } from '../../../components/Filter/store/thunk';
import { loadSingleTransaction } from 'pages/Transactions/store/thunk';
import { loadSellOrder } from 'pages/SellOrders/store/thunk';
import { setServiceOrderId } from '../../../components/ServiceOrder/store/actions';
import { toggleServiceOrder } from '../../OperationalHistory/store/actions';
import { setClientId } from '../../ClientRegistration/components/steps/BasicInfo/store/actions';
import { setEstablishmentId } from '../../BusinessInfo/store/actions';
import { setInvoiceId } from '../../ClientInvoices/store/actions';
import { handleRegistration, setTransactionDetails } from '../../Transactions/store/actions';
import { setSourceId } from '../store/actions';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { inputs } from './resources';

const headers = [
  { title: 'Número da nota', field: 'nfseNumber', sortable: false },
  { title: 'Série e N˚(RPS)', field: 'rpsSerieAndNumber', sortable: false },
  { title: 'Emissão', field: 'nfDateTime', sortable: false },
  { title: 'Competência', field: 'paymentDateTimeView', sortable: false },
  { title: 'Status', field: 'nfseStatus', sortable: true, defaultSort: 'desc' },
  { title: 'Valor', field: 'amountView', sortable: false },
  { title: 'Ações', field: '', sortable: false }
];

export default function ProvisionalReceipt() {
  const dispatch = useDispatch();

  const history = useHistory();

  const { width } = useWindowDimensions();
  const isMobile = width < 1024;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId, establishmentTypes } = useSelector(state => state.businessInfo);
  const { establishments } = useSelector(state => state.login);
  const { modal } = useSelector(state => state.operationalHistory);
  const { registration, submit: { transactionTypeId } } = useSelector(state => state.transactions);
  const { selects, qp, isSearching, isFilterButtonClicked } = useSelector(state => state.filters);
  const { startDate, endDate } = useSelector(state => state.dateFilter);
  const { list, total, resume, isLoading, isResumeLoading, error, selectedNfWithErrors, selectedNfWithErrorsPaymentDateTime, isSendNfModalOpen } = useSelector(state => state.provisionalReceipt);

  const filters = concat(inputs, formatFilters(['nfseStatus'], selects))?.map(filter => {
    if(filter.id == 'nfseStatus') {
      return {
        ...filter,
        id: 'nfStatus'
      }
    }

    return filter;
  });

  const nfStatusList = selects?.find(filter => filter?.id == 'nfseStatus')?.options;
  const convertedList = convertList(['nfseStatus'], filters, list);

  const defaultEstablishmentTypes = establishmentTypes?.map(establishment => +establishment) || [];
  const isStoreOnly = defaultEstablishmentTypes.length == 1 && +defaultEstablishmentTypes[0] == 3

  const [isOpenInvoice, setIsOpenInvoice] = useState(false);
  const [isOpenInvoiceServiceContract, setIsOpenInvoiceServiceContract] = useState(false);
  const [isOpenSellOrder, setIsOpenSellOrder] = useState(false);
  const [calculatedInvoiceDateTime, setCalculatedInvoiceDateTime] = useState('');
  const [provisionalReceiptResendStartTime, setProvisionalReceiptResendStartTime] = useState(-1);
  const [provisionalReceiptResendTime, setProvisionalReceiptResendTime] = useState(-1);
  const [confirmResendNfseDateLimit, setConfirmResendNfseDateLimit] = useState(false);
  const [confirmResendNfse, setConfirmResendNfse] = useState(false);
  const [cancelReceipt, setCancelReceipt] = useState(null);

  const [provisionalReceiptPage, setProvisionalReceiptPage] = useState(1);
  const provisionalReceiptPageSize = 10;
  const provisionalReceiptPageCount = Math.ceil(total / provisionalReceiptPageSize);

  const handleProvisionalReceiptList = ({ page, qp }) => {
    const extraProps = {
      startDate,
      endDate
    }

    if(establishmentId) {
      dispatch(loadProvisionalReceipt({ userId, establishmentId, page, pageSize: provisionalReceiptPageSize, qp, extraProps }));
      dispatch(loadProvisionalReceiptResume(userId, establishmentId, qp, extraProps));
    }
  }

  useEffect(() => {
    if(provisionalReceiptResendTime > 0) {
      setTimeout(() => {
        setProvisionalReceiptResendTime(provisionalReceiptResendTime - 1);
      }, 1000);
    }
  }, [provisionalReceiptResendTime]);

  useEffect(() => {
    if(provisionalReceiptResendTime == 0) {
      setProvisionalReceiptResendStartTime(-1);
      setProvisionalReceiptResendTime(-1);
      handleProvisionalReceiptList({ page: provisionalReceiptPage, qp });
      dispatch(setIsNfSendModalOpen(false));
    }
  }, [provisionalReceiptResendTime]);

  useEffect(() => {
    dispatch(handleError({ error: null, uuid: null, transactionId: null }));
  }, []);

  useEffect(() => {
    if(startDate !== 'Invalid date' && endDate !== 'Invalid date') {
      handleProvisionalReceiptList({ page: 1, qp });
      dispatch(loadFilters(userId, establishmentId));
    }
  }, [establishmentId, startDate, endDate]);

  useEffect(() => {
    handleProvisionalReceiptList({ page: provisionalReceiptPage, qp });
  }, [provisionalReceiptPage]);

  useEffect(() => {
    setProvisionalReceiptPage(1);
  }, [establishmentId]);

  useEffect(() => {
    if(isSearching) {
      setProvisionalReceiptPage(1);
    }
  }, [isSearching]);

  useEffect(() => {
    if(isFilterButtonClicked) {
      setProvisionalReceiptPage(1);
    }
  }, [isFilterButtonClicked]);

  useEffect(() => {
    if(isSearching || establishmentId) {
      dispatch(clearNfWithErrors());
    }
  }, [isSearching, establishmentId]);

  useEffect(() => {
    const list = convertedList
      ?.filter(item => selectedNfWithErrors?.includes(item?.sourceId))
      ?.map(item => {
        return {
          sourceId: item?.sourceId,
          paymentDateTime: item?.paymentDateTime,
          rpsNumber: item?.number,
          rpsSeries: item?.series,
          nfse: item?.nfse,
          nfce: item?.nfce
        }
      });

    const uniqueList = uniqBy([...selectedNfWithErrorsPaymentDateTime, ...list], function(e) {
      return e.sourceId;
    });

    const updatedList = uniqueList
      ?.filter(item => selectedNfWithErrors?.includes(item?.sourceId));

    dispatch(setSelectedNfWithErrorsPaymentDateTime(updatedList));
  }, [selectedNfWithErrors]);

  const getNfseStatus = item => {
    if(item?.nfStatus == 6) {
      return 'Sincronizando';
    }

    if(!!item?.nfCancelProtocol && +item?.nfStatus == 2) {
      return(
        <>
          Emitada com sucesso
          <br />
          <Typography variant="caption" color="textSecondary">Cancelamento rejeitado</Typography>
        </>
      );
    }

    return nfStatusList?.find(nf => +nf?.value == +item?.nfStatus)?.label || '--';
  }

  const getEstablishmentId = establishmentUuid => {
    const establishmentId = establishments
      ?.find(establishment => establishment?.uuid == establishmentUuid)
      ?.establishmentId;

    return establishmentId || '';
  }

  const getClientId = sourceId => {
    if(sourceId) {
      return sourceId?.slice(0, sourceId?.length - 14);
    }

    return '';
  }

  const getInvoiceDateTime = sourceId => {
    if(sourceId) {
      return sourceId?.slice(sourceId?.length - 14, sourceId?.length);
    }

    return '';
  }

  const getDocumentType = item => {
    const { nfse, nfe } = item || {};

    if(+nfse == 1) {
      return '(NFSe)';
    }

    if(+nfe == 1) {
      return '(NFCe)';
    }

    return '';
  }

  const handleProvisionalReceiptLink = () => {
    if(isStoreOnly) {
      return history.push('/configuracoes-de-notas-fiscais-de-produto');
    }

    if(!defaultEstablishmentTypes?.includes(3)) {
      return history.push('/configuracoes-de-notas-fiscais');
    }

    if(defaultEstablishmentTypes?.includes(3) && defaultEstablishmentTypes.length > 1) {
      return history.push('/tipo-de-nota-fiscal');
    }
  }

  const handleTransactionRegistration = async (status, modalType) => {
    dispatch(handleRegistration(status, modalType));
  }

  const selectTransactionType = async (categorySourceId, clientId, sourceId, establishmentId) => {
    dispatch(setSourceId(sourceId));

    if(+categorySourceId == 1) {
      await dispatch(setServiceOrderId(sourceId, userId, establishmentId));
      await dispatch(toggleServiceOrder(true));
    }

    if(+categorySourceId == 2) {
      await dispatch(setClientId(clientId));
      await dispatch(setEstablishmentId({ establishmentId }));
      await dispatch(setInvoiceId(getInvoiceDateTime(sourceId)));
      setIsOpenInvoice(true);
    }

    if(+categorySourceId == 3) {
      const singleTransactionData = await dispatch(loadSingleTransaction(userId, establishmentId, sourceId));

      if(singleTransactionData) {
        const { transactionTypeId } = singleTransactionData;

        await handleTransactionRegistration(true, +transactionTypeId).then(async () => {
          await dispatch(setTransactionDetails({ ...singleTransactionData }));
        });
      }
    }

    if(+categorySourceId == 8) {
      await dispatch(setClientId(clientId));
      setCalculatedInvoiceDateTime(getInvoiceDateTime(sourceId));
      setIsOpenInvoiceServiceContract(true);
    }

    if(+categorySourceId == 11) {
      await dispatch(loadSellOrder(userId, establishmentId, sourceId));
      await setIsOpenSellOrder(true);
    }
  }

  const onSelectRow = row => {
    const { sourceId } = row;
    dispatch(setSelectedSingleNfWithErrors(sourceId));
  }

  const onSelectAllRows = () => {
    const isAllRowsChecked = convertedList
      ?.filter(item => +item?.nfseStatusNumber == 0 || +item?.nfseStatusNumber == 4)
      ?.map(item => item?.sourceId)
      ?.every(item => selectedNfWithErrors?.includes(item));

    const list = convertedList
      ?.filter(item => +item?.nfseStatusNumber == 0 || +item?.nfseStatusNumber == 4)
      ?.map(item => item?.sourceId);

    if(isAllRowsChecked) {
      return dispatch(clearPageNfWithErrors(list));
    }

    dispatch(setSelectedNfWithErrors([...new Set(list)]));
  }

  return(
    <>
      {isSendNfModalOpen && (
        <Modal
          title="Deseja reenviar essas notas fiscais?"
          open={isSendNfModalOpen || (provisionalReceiptResendTime > 0)}
          onClose={() => provisionalReceiptResendTime > 0 ? null : dispatch(setIsNfSendModalOpen(false))}
        >
          {(provisionalReceiptResendTime > 0) ? (
            <Grid container spacing={2}>
              <Grid xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography color="primary">
                  Notas em processo de reenvio
                </Typography>
                {(provisionalReceiptResendTime > 0) && (
                  <LoaderLabel value={relativePercentage(provisionalReceiptResendTime, provisionalReceiptResendStartTime)}>
                    {provisionalReceiptResendTime}
                  </LoaderLabel>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid xs={12} style={{ padding: 5 }}>
                <Typography color="primary">
                  <b>Total: {selectedNfWithErrorsPaymentDateTime?.length}</b>
                </Typography>
                <Typography color="primary">
                  Notas fiscais: {selectedNfWithErrorsPaymentDateTime?.map(item => `${item?.rpsNumber > 0 ? `${item?.rpsSeries}-${item?.rpsNumber}` : ''}`.trim())?.join(', ')}
                </Typography>
                <div style={{ margin: '15px 0 15px 0' }}>
                  <Typography color="textSecondary">
                    Verifique a data limite do envio de nota fiscal no seu município para evitar multas da receita federal. (NFe e NFSe).
                  </Typography>
                  <FormControlLabel
                    labelPlacement="end"
                    label={
                      <Typography color="primary">
                        Entendi
                      </Typography>
                    }
                    control={
                      <Checkbox
                        value={confirmResendNfseDateLimit}
                        onChange={event => setConfirmResendNfseDateLimit(event.target.checked)}
                      />
                    }
                  />
                </div>
                {selectedNfWithErrorsPaymentDateTime?.some(item => moment(moment.now()).diff(moment(item?.paymentDateTime, 'YYYY-MM-DD'), 'days') > 5) && (
                  <div style={{ margin: '15px 0 15px 0' }}>
                    <WarningMessage
                      textAlign="left"
                      message="Atenção! Mais de 5 dias se passaram desde o pagamento, fique atento a uma possível cobrança de multa por parte da prefeitura."
                    />
                    <FormControlLabel
                      labelPlacement="end"
                      label={
                        <Typography color="primary">
                          Entendi
                        </Typography>
                      }
                      control={
                        <Checkbox
                          value={confirmResendNfse}
                          onChange={event => setConfirmResendNfse(event.target.checked)}
                        />
                      }
                    />
                  </div>
                )}
              </Grid>
              <Grid xs={6} style={{ padding: 5 }}>
                <Button
                  fullWidth
                  color="error"
                  onClick={() => dispatch(setIsNfSendModalOpen(false))}
                >
                  Cancelar
                </Button>
              </Grid>
              <Grid xs={6} style={{ padding: 5 }}>
                <Button
                  disabled={(selectedNfWithErrorsPaymentDateTime?.some(item => moment(moment.now()).diff(moment(item?.paymentDateTime, 'YYYY-MM-DD'), 'days') > 5) && !confirmResendNfse) || !confirmResendNfseDateLimit}
                  loading={isLoading}
                  fullWidth
                  color="primary"
                  onClick={() => {
                    const nfList = selectedNfWithErrorsPaymentDateTime
                      ?.sort((a, b) => moment(a?.paymentDateTime, 'DD-MM-YYYY HH:mm:ss').diff(moment(b?.paymentDateTime, 'DD-MM-YYYY HH:mm:ss')))
                      ?.map(item => {
                        return {
                          sourceId: item?.sourceId,
                          ...+item?.nfse == 1 && { nfse: item?.nfse },
                          ...+item?.nfce == 1 && { nfce: item?.nfce }
                        }
                      });

                    dispatch(resendMultipleNfse(userId, establishmentId, nfList)).then(() => {
                      const resendTime = selectedNfWithErrorsPaymentDateTime?.length > 10
                        ? selectedNfWithErrorsPaymentDateTime?.length * 0.5
                        : selectedNfWithErrorsPaymentDateTime?.length;

                      setProvisionalReceiptResendStartTime(resendTime);
                      setProvisionalReceiptResendTime(resendTime);
                    });
                  }}
                >
                  Confirmar
                </Button>
              </Grid>
            </Grid>
          )}
        </Modal>
      )}
      <Resume
        resume={resume}
        isLoading={isResumeLoading}
      />
      <Paper>
        {modal && (
          <ServiceOrder
            modal={modal}
            establishmentId={establishmentId}
          />
        )}
        {(registration && +transactionTypeId == 1) && (
          <Receipt
            isOpen={registration}
            establishmentId={establishmentId}
          />
        )}
        {(registration && +transactionTypeId == 2) && (
          <Payment
            isOpen={registration}
            establishmentId={establishmentId}
          />
        )}
        {isOpenInvoice && (
          <Invoice
            isOpen={isOpenInvoice}
            setIsOpenInvoice={setIsOpenInvoice}
            establishmentId={establishmentId}
          />
        )}
        {isOpenInvoiceServiceContract && (
          <InvoiceServiceContract
            isOpenInvoiceServiceContract={isOpenInvoiceServiceContract}
            setIsOpenInvoiceServiceContract={setIsOpenInvoiceServiceContract}
            establishmentId={establishmentId}
            calculatedInvoiceDateTime={calculatedInvoiceDateTime}
            isInvoicePaid={true}
          />
        )}
        {isOpenSellOrder && (
          <SellOrder
            isSellOrderModalOpen={isOpenSellOrder}
            setIsSellOrderModalOpen={setIsOpenSellOrder}
            establishmentId={establishmentId}
            pageOrigin="/notas-fiscais"
          />
        )}
        <ProvisionalReceiptError
          open={!!error}
          error={error}
          page={provisionalReceiptPage}
          pageSize={provisionalReceiptPageSize}
          qp={qp}
          extraProps={{ startDate, endDate }}
        />
        <ProvisionalReceiptCancel
          open={!!cancelReceipt}
          receipt={cancelReceipt}
          page={provisionalReceiptPage}
          pageSize={provisionalReceiptPageSize}
          qp={qp}
          extraProps={{ startDate, endDate }}
          onClose={() => setCancelReceipt(null)}
        />
        {isMobile ? (
          <FilterMobile
            filter
            hasSearchTerm
            undoFilter
            actionButton={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                <Button
                  fullWidth
                  type="button"
                  color="tertiary"
                  variant="outlined"
                  onClick={handleProvisionalReceiptLink}
                  startIcon={
                    <FontAwesomeIcon
                      size="xs"
                      color="#757575"
                      icon={faCog}
                    />
                  }
                >
                  Configurar
                </Button>
                {(selectedNfWithErrors?.length > 0) && (
                  <Button
                    fullWidth
                    type="button"
                    color="tertiary"
                    variant="outlined"
                    onClick={() => dispatch(setIsNfSendModalOpen(true))}
                    startIcon={
                      <FontAwesomeIcon
                        size="xs"
                        color="#757575"
                        icon={faArrowsRotate}
                      />
                    }
                  >
                    Reenviar notas com erro
                  </Button>
                )}
              </div>
            }
            filters={filters}
            handlePageRequest={handleProvisionalReceiptList}
          />
        ) : (
          <Filter
            filter
            hasSearchTerm
            actionButton={
              <div style={{ display: 'flex', gap: 10 }}>
                <Button
                  type="button"
                  color="primary"
                  onClick={handleProvisionalReceiptLink}
                >
                  Configurações das notas fiscais
                </Button>
                {(selectedNfWithErrors?.length > 0) && (
                  <Button
                    type="button"
                    onClick={() => dispatch(setIsNfSendModalOpen(true))}
                  >
                    Reenviar notas fiscais
                  </Button>
                )}
              </div>
            }
            filters={filters}
            handlePageRequest={handleProvisionalReceiptList}
          />
        )}
        {isMobile && (
          <TableMobile
            loading={isLoading}
            selectCard
            onSelectCard={item => onSelectRow(item)}
            data={convertedList?.map(item => {
              return {
                ...item,
                checked: selectedNfWithErrors?.includes(item?.sourceId),
                disabledChecked: item?.nfseStatusNumber != 0 && item?.nfseStatusNumber != 4,
                nfseNumber: {
                  index: 1,
                  label: 'Número da nota',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.nfseNumber}</Typography>
                },
                rpsSerieAndNumber: {
                  index: 2,
                  label: 'Série e N˚(RPS)',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.rpsSerieAndNumber}</Typography>
                },
                nfDateTime: {
                  index: 3,
                  label: 'Emissão',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.nfDateTime}</Typography>
                },
                paymentDateTimeView: {
                  index: 4,
                  label: 'Competência',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.paymentDateTimeView}</Typography>
                },
                nfseStatus: {
                  index: 5,
                  label: 'Status',
                  value: <Typography color='textSecondary' variant='subtitle2'>{getNfseStatus(item)}</Typography>
                },
                amountView: {
                  index: 6,
                  label: 'Valor',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.amountView}</Typography>
                }
              }
            })}
            actions={[
              {
                action: rowData => (+rowData?.nfseStatusNumber == 0 || +rowData?.nfseStatusNumber == 4) && (
                  <IconButton
                    size="medium"
                    onClick={() => {
                      dispatch(
                        handleError({
                          error: rowData?.nfResult ? rowData?.nfResult : ' ',
                          uuid: rowData?.uuid,
                          nfse: rowData?.nfse,
                          nfce: rowData?.nfce,
                          paymentDateTime: rowData?.paymentDateTime,
                          transactionId: rowData?.sourceId,
                          establishmentId: getEstablishmentId(rowData?.establishmentUuid)
                        })
                      )
                    }}
                  >
                    <FontAwesomeIcon
                      size="xs"
                      color="#F66D6E"
                      icon={faExclamationCircle}
                    />
                  </IconButton>
                )
              },
              {
                action: rowData => (
                  <IconButton
                    size="medium"
                    onClick={() => selectTransactionType(rowData?.categorySourceId, getClientId(rowData?.sourceId), rowData?.sourceId, getEstablishmentId(rowData?.establishmentUuid))}
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      color="#022A5C"
                      size="xs"
                    />
                  </IconButton>
                )
              },
              {
                action: rowData => (+rowData?.nfStatus == 2) && (
                  <IconButton
                    size="medium"
                    onClick={() => {
                      const params = {
                        nfse: rowData?.nfse,
                        nfce: rowData?.nfce
                      }

                      dispatch(loadDownloadNfse(userId, getEstablishmentId(rowData?.establishmentUuid), rowData?.uuid, params));
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faDownload}
                      color="#022A5C"
                      size="xs"
                    />
                  </IconButton>
                )
              },
              {
                action: rowData => (+rowData?.nfseStatusNumber == 2) && (
                  <IconButton
                    size="medium"
                    onClick={() => setCancelReceipt(rowData)}
                  >
                    <FontAwesomeIcon
                      size="xs"
                      color="#F66D6E"
                      icon={faTimesCircle}
                    />
                  </IconButton>
                )
              }
            ]}
          />
        )}
        {!isMobile && (
          <>
            {isLoading ? (
              <>
                <TableContainer>
                  <Table>
                    <TableHead style={{ backgroundColor: '#022A5C' }}>
                      <TableRow>
                        {headers.map(item => {
                          return(
                            <TableCell style={{ color: '#FFFFFF' }}>{item.title}</TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                  <CircularProgress color='primary' size={30} />
                </div>
              </>
            ) : (
              <>
                {convertedList?.length == 0 && (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead style={{ backgroundColor: '#022A5C' }}>
                          <TableRow>
                            {headers.map(item => {
                              return(
                                <TableCell style={{ color: '#FFFFFF' }}>{item.title}</TableCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                      </Table>
                    </TableContainer>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 10 }}>
                      <Typography color='primary' variant='body2'>Não foram encontrados itens nessa tabela</Typography>
                    </div>
                  </>
                )}
                {convertedList?.length > 0 && (
                  <TableContainer>
                    <Table>
                      <TableHead style={{ backgroundColor: '#022A5C' }}>
                        <TableRow>
                          <TableCell style={{ textAlign: 'center' }}>
                            {convertedList?.filter(item => +item?.nfseStatusNumber == 0 || +item?.nfseStatusNumber == 4)?.length > 0 && (
                              <Checkbox
                                style={{ color: '#FFFFFF' }}
                                checked={
                                  convertedList
                                    ?.filter(item => +item?.nfseStatusNumber == 0 || +item?.nfseStatusNumber == 4)
                                    ?.map(item => item?.sourceId)
                                    ?.every(item => selectedNfWithErrors?.includes(item))
                                }
                                onChange={onSelectAllRows}
                              />
                            )}
                          </TableCell>
                          {headers.map(item => {
                            return(
                              <TableCell style={{ textAlign: 'center', color: '#FFFFFF' }}>{item.title}</TableCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {convertedList?.map(item => {
                          return(
                            <TableRow>
                              <TableCell style={{ textAlign: 'center' }}>
                                <Checkbox
                                  color='primary'
                                  disabled={+item?.nfseStatusNumber != 0 && +item?.nfseStatusNumber != 4}
                                  checked={selectedNfWithErrors?.includes(item?.sourceId)}
                                  onChange={() => onSelectRow(item)}
                                />
                              </TableCell>
                              <TableCell style={{ textAlign: 'center' }}>{item?.nfseNumber} {getDocumentType(item)}</TableCell>
                              <TableCell style={{ textAlign: 'center' }}>{item?.rpsSerieAndNumber}</TableCell>
                              <TableCell style={{ textAlign: 'center' }}>{item?.nfDateTime}</TableCell>
                              <TableCell style={{ textAlign: 'center' }}>{item?.paymentDateTimeView}</TableCell>
                              <TableCell style={{ textAlign: 'center' }}>{getNfseStatus(item)}</TableCell>
                              <TableCell style={{ textAlign: 'center' }}>{item?.amountView}</TableCell>
                              <TableCell style={{ textAlign: 'center' }}>
                                {(+item?.nfseStatusNumber == 0 || +item?.nfseStatusNumber == 4) && (
                                  <IconButton
                                    size="medium"
                                    onClick={() => {
                                      dispatch(
                                        handleError({
                                          error: item?.nfResult ? item?.nfResult : ' ',
                                          uuid: item?.uuid,
                                          nfse: item?.nfse,
                                          nfce: item?.nfce,
                                          paymentDateTime: item?.paymentDateTime,
                                          transactionId: item?.sourceId,
                                          establishmentId: getEstablishmentId(item?.establishmentUuid)
                                        })
                                      )
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      size="xs"
                                      color="#F66D6E"
                                      icon={faExclamationCircle}
                                    />
                                  </IconButton>
                                )}
                                <IconButton
                                  size="medium"
                                  onClick={() => selectTransactionType(item?.categorySourceId, getClientId(item?.sourceId), item?.sourceId, getEstablishmentId(item?.establishmentUuid))}
                                >
                                  <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    color="#022A5C"
                                    size="xs"
                                  />
                                </IconButton>
                                {(+item?.nfStatus == 2) && (
                                  <IconButton
                                    size="medium"
                                    onClick={() => {
                                      const params = {
                                        nfse: item?.nfse,
                                        nfce: item?.nfce
                                      }

                                      dispatch(loadDownloadNfse(userId, getEstablishmentId(item?.establishmentUuid), item?.uuid, params));
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faDownload}
                                      color="#022A5C"
                                      size="xs"
                                    />
                                  </IconButton>
                                )}
                                {(+item?.nfseStatusNumber == 2) && (
                                  <IconButton
                                    size="medium"
                                    onClick={() => setCancelReceipt(item)}
                                  >
                                    <FontAwesomeIcon
                                      size="xs"
                                      color="#F66D6E"
                                      icon={faTimesCircle}
                                    />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </>
        )}
        <Grid
          container
          xs={12}
          style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
        >
          <Pagination
            color="primary"
            variant="outlined"
            shape="rounded"
            count={provisionalReceiptPageCount}
            page={provisionalReceiptPage}
            onChange={(_, page) => setProvisionalReceiptPage(page)}
          />
        </Grid>
      </Paper>
    </>
  );
}